import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  FileBarChart, 
  Download, 
  Calendar, 
  Loader2, 
  RefreshCw, 
  Users, 
  Building, 
  FileText,
  MessageSquare,
  TrendingUp,
  Mail,
  Clock,
  CheckCircle,
  Save
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel } from '@/utils/exportExcel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActivityReportsPage = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [schedule, setSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    enabled: false,
    frequency: 'weekly',
    recipients: ''
  });
  const [savingSchedule, setSavingSchedule] = useState(false);

  useEffect(() => {
    fetchReport();
    fetchSchedule();
  }, [period]);

  const fetchReport = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/activity-report?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load activity report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedule = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/admin/activity-report/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSchedule(response.data);
        setScheduleForm({
          enabled: response.data.enabled || false,
          frequency: response.data.frequency || 'weekly',
          recipients: (response.data.recipients || []).join(', ')
        });
      }
    } catch (error) {
      console.error('Failed to load schedule:', error);
    }
  };

  const handleSaveSchedule = async () => {
    const token = localStorage.getItem('token');
    setSavingSchedule(true);
    try {
      const recipients = scheduleForm.recipients
        .split(',')
        .map(e => e.trim())
        .filter(e => e.length > 0);
      
      await axios.post(
        `${API}/admin/activity-report/schedule`,
        {
          enabled: scheduleForm.enabled,
          frequency: scheduleForm.frequency,
          recipients
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Schedule saved successfully');
      fetchSchedule();
    } catch (error) {
      toast.error('Failed to save schedule');
      console.error(error);
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleExportReport = () => {
    if (!report) return;

    const exportData = [
      { 'Metric': 'Report Period', 'Value': report.period_label },
      { 'Metric': 'Generated At', 'Value': new Date(report.generated_at).toLocaleString() },
      { 'Metric': '', 'Value': '' },
      { 'Metric': '--- USER ENGAGEMENT ---', 'Value': '' },
      { 'Metric': 'Total Activities', 'Value': report.user_engagement?.total_activities || 0 },
      { 'Metric': 'Unique Active Users', 'Value': report.user_engagement?.unique_active_users || 0 },
      { 'Metric': 'Logins', 'Value': report.user_engagement?.logins || 0 },
      { 'Metric': '', 'Value': '' },
      { 'Metric': '--- GROWTH ---', 'Value': '' },
      { 'Metric': 'New Users', 'Value': report.growth?.new_users || 0 },
      { 'Metric': 'New Companies', 'Value': report.growth?.new_companies || 0 },
      { 'Metric': '', 'Value': '' },
      { 'Metric': '--- BUSINESS ACTIVITY ---', 'Value': '' },
      { 'Metric': 'New Reservations', 'Value': report.business_activity?.new_reservations || 0 },
      { 'Metric': 'Accepted Reservations', 'Value': report.business_activity?.accepted_reservations || 0 },
      { 'Metric': 'New Collaborations', 'Value': report.business_activity?.new_collaborations || 0 },
      { 'Metric': 'New Contracts', 'Value': report.business_activity?.new_contracts || 0 },
      { 'Metric': 'Signed Contracts', 'Value': report.business_activity?.signed_contracts || 0 },
      { 'Metric': '', 'Value': '' },
      { 'Metric': '--- COMMUNICATION ---', 'Value': '' },
      { 'Metric': 'New Messages', 'Value': report.communication?.new_messages || 0 },
      { 'Metric': 'New Support Tickets', 'Value': report.communication?.new_support_tickets || 0 }
    ];

    exportToExcel(exportData, `activity_report_${period}_${new Date().toISOString().split('T')[0]}`, 'Activity Report');
    toast.success('Report exported to Excel');
  };

  const StatCard = ({ title, value, icon: Icon, color = 'text-gray-600', bgColor = 'bg-gray-100' }) => (
    <div className="p-4 bg-white rounded-lg border">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
        </div>
        <div className={`w-10 h-10 ${bgColor} rounded-full flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Activity Reports</h1>
          <p className="text-gray-600 mt-2">Generate and export platform activity reports</p>
        </div>
        <div className="flex gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Last 24 Hours</SelectItem>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchReport} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={handleExportReport} disabled={!report} style={{ backgroundColor: '#62D84E' }}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : report ? (
        <div className="space-y-6">
          {/* Report Info */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <FileBarChart className="w-8 h-8 text-primary" />
                  <div>
                    <h3 className="font-semibold">{report.period_label}</h3>
                    <p className="text-sm text-gray-500">
                      {new Date(report.start_date).toLocaleDateString()} - {new Date(report.end_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  Generated: {new Date(report.generated_at).toLocaleString()}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* User Engagement */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="w-5 h-5 mr-2 text-blue-600" />
                User Engagement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard
                  title="Total Activities"
                  value={report.user_engagement?.total_activities || 0}
                  icon={TrendingUp}
                  color="text-blue-600"
                  bgColor="bg-blue-100"
                />
                <StatCard
                  title="Unique Active Users"
                  value={report.user_engagement?.unique_active_users || 0}
                  icon={Users}
                  color="text-green-600"
                  bgColor="bg-green-100"
                />
                <StatCard
                  title="Logins"
                  value={report.user_engagement?.logins || 0}
                  icon={CheckCircle}
                  color="text-purple-600"
                  bgColor="bg-purple-100"
                />
                <div className="p-4 bg-white rounded-lg border">
                  <p className="text-sm text-gray-500 mb-2">Activities by Type</p>
                  <div className="space-y-1">
                    {Object.entries(report.user_engagement?.activities_by_type || {}).slice(0, 4).map(([action, count]) => (
                      <div key={action} className="flex justify-between text-sm">
                        <span className="text-gray-600">{action.replace(/_/g, ' ')}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Growth & Business */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                  Platform Growth
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <StatCard
                    title="New Users"
                    value={report.growth?.new_users || 0}
                    icon={Users}
                    color="text-green-600"
                    bgColor="bg-green-100"
                  />
                  <StatCard
                    title="New Companies"
                    value={report.growth?.new_companies || 0}
                    icon={Building}
                    color="text-blue-600"
                    bgColor="bg-blue-100"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-orange-600" />
                  Business Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <StatCard
                    title="New Reservations"
                    value={report.business_activity?.new_reservations || 0}
                    icon={Calendar}
                    color="text-orange-600"
                    bgColor="bg-orange-100"
                  />
                  <StatCard
                    title="Accepted"
                    value={report.business_activity?.accepted_reservations || 0}
                    icon={CheckCircle}
                    color="text-green-600"
                    bgColor="bg-green-100"
                  />
                  <StatCard
                    title="New Collaborations"
                    value={report.business_activity?.new_collaborations || 0}
                    icon={Users}
                    color="text-purple-600"
                    bgColor="bg-purple-100"
                  />
                  <StatCard
                    title="Signed Contracts"
                    value={report.business_activity?.signed_contracts || 0}
                    icon={FileText}
                    color="text-blue-600"
                    bgColor="bg-blue-100"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Communication */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-purple-600" />
                Communication
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <StatCard
                  title="New Messages"
                  value={report.communication?.new_messages || 0}
                  icon={MessageSquare}
                  color="text-purple-600"
                  bgColor="bg-purple-100"
                />
                <StatCard
                  title="Support Tickets"
                  value={report.communication?.new_support_tickets || 0}
                  icon={Mail}
                  color="text-red-600"
                  bgColor="bg-red-100"
                />
              </div>
            </CardContent>
          </Card>

          {/* Automatic Reports Schedule */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="w-5 h-5 mr-2 text-gray-600" />
                Automatic Report Schedule
              </CardTitle>
              <CardDescription>Configure automatic email reports</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Enable Automatic Reports</p>
                  <p className="text-sm text-gray-500">Receive activity reports via email</p>
                </div>
                <Switch
                  checked={scheduleForm.enabled}
                  onCheckedChange={(checked) => setScheduleForm({...scheduleForm, enabled: checked})}
                />
              </div>

              {scheduleForm.enabled && (
                <>
                  <div>
                    <Label>Frequency</Label>
                    <Select
                      value={scheduleForm.frequency}
                      onValueChange={(value) => setScheduleForm({...scheduleForm, frequency: value})}
                    >
                      <SelectTrigger className="w-full mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Recipients (comma-separated emails)</Label>
                    <Input
                      value={scheduleForm.recipients}
                      onChange={(e) => setScheduleForm({...scheduleForm, recipients: e.target.value})}
                      placeholder="admin@example.com, manager@example.com"
                      className="mt-1"
                    />
                  </div>

                  {schedule?.next_send && (
                    <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
                      <strong>Next scheduled report:</strong> {new Date(schedule.next_send).toLocaleString()}
                    </div>
                  )}
                </>
              )}

              <Button
                onClick={handleSaveSchedule}
                disabled={savingSchedule}
                style={{ backgroundColor: '#62D84E' }}
              >
                {savingSchedule ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Schedule
              </Button>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No report data available
        </div>
      )}
    </div>
  );
};

export default ActivityReportsPage;
